import { useState, useEffect } from "react";
import { Menu, X, Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Function to determine if a link is active
  const isActive = (path: string) => {
    // For home page, only exact match
    if (path === '/') {
      return location.pathname === path;
    }
    // For other pages, check if pathname starts with the path
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'backdrop-blur-xl bg-black/90' : 'backdrop-blur-sm bg-black/70'
      }`}
    >
      {/* Gold accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-gold to-transparent opacity-50"></div>
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 py-3 flex justify-between items-center relative z-10">
        <Link to="/" className="flex items-center group">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            {/* Gold glow effect on hover */}
            <div className="absolute -inset-2 bg-gold/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <img 
              src="/optigold-logo-color.svg" 
              alt="OptiGold Logo" 
              className="h-20 relative z-10 filter brightness-110 contrast-125" // Enhanced logo visibility
            />
          </motion.div>
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden lg:flex items-center space-x-10">
          {[
            { path: '/', label: 'Home' },
            { path: '/about', label: 'About' },
            { path: '/services', label: 'Services' },
            { path: '/assessment', label: 'Assessment' },
            { path: '/contact', label: 'Contact' },
          ].map((item) => (
            <motion.div
              key={item.path}
              whileHover={{ y: -2 }}
              className="relative"
            >
              <Link 
                to={item.path}
                className={`relative font-semibold text-lg tracking-wide transition-all duration-300 ${
                  isActive(item.path) 
                    ? 'text-gold' 
                    : 'text-gray-300 hover:text-gold'
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <motion.span
                    layoutId="navbar-indicator"
                    className="absolute -bottom-2 left-0 right-0 h-0.5 bg-gradient-to-r from-gold to-amber-400"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                )}
                {/* Hover glow */}
                <span className="absolute -inset-2 bg-gold/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity blur-sm -z-10" />
              </Link>
            </motion.div>
          ))}
          
          {/* Blog link */}
          <motion.div
            whileHover={{ y: -2 }}
            className="relative"
          >
            <a 
              href="https://blog.optigoldpmo.com" 
              className="relative font-semibold text-lg tracking-wide text-gray-300 hover:text-gold transition-all duration-300"
            >
              Blog
              <span className="absolute -inset-2 bg-gold/10 rounded-lg opacity-0 hover:opacity-100 transition-opacity blur-sm -z-10" />
            </a>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link
              to="/contact"
              className="relative inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gold to-amber-400 text-black font-bold rounded-full shadow-lg hover:shadow-gold/50 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              Get Started
            </Link>
          </motion.div>
        </nav>

        {/* Mobile Menu Toggle */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="lg:hidden text-gold focus:outline-none relative"
          onClick={toggleMobileMenu}
        >
          <AnimatePresence mode="wait">
            {isMobileMenuOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <X size={28} />
              </motion.div>
            ) : (
              <motion.div
                key="menu"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu size={28} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
          >
            <div className="bg-black/95 backdrop-blur-xl border-t border-gold/20">
              <div className="container mx-auto px-4 py-6 flex flex-col space-y-4">
                {[
                  { path: '/', label: 'Home' },
                  { path: '/about', label: 'About' },
                  { path: '/services', label: 'Services' },
                  { path: '/assessment', label: 'Assessment' },
                  { path: '/contact', label: 'Contact' },
                ].map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Link
                      to={item.path}
                      className={`block py-3 px-4 rounded-lg font-semibold text-lg transition-all ${
                        isActive(item.path)
                          ? 'text-gold bg-gold/10'
                          : 'text-gray-300 hover:text-gold hover:bg-gold/5'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="flex items-center justify-between">
                        {item.label}
                        {isActive(item.path) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-2 h-2 rounded-full bg-gold"
                          />
                        )}
                      </span>
                    </Link>
                  </motion.div>
                ))}
                
                {/* Blog link */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.25 }}
                >
                  <a
                    href="https://blog.optigoldpmo.com"
                    className="block py-3 px-4 rounded-lg font-semibold text-lg text-gray-300 hover:text-gold hover:bg-gold/5 transition-all"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Blog
                  </a>
                </motion.div>

                {/* Mobile CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="pt-4"
                >
                  <Link
                    to="/contact"
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-gold to-amber-400 text-black font-bold rounded-full shadow-lg hover:shadow-gold/50 transition-all"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Get Started
                    </span>
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
